/**
 * Default factory functions for creating stage layout components
 * Uses uuid v4 for ID generation and sensible defaults
 */

import { v4 as uuidv4 } from 'uuid';
import {
  StageLayout,
  CanvasConfig,
  StageObject,
  StageObjectType,
  ObjectVariant,
  ShootingPosition,
  ShootingPositionType,
  StartCondition,
  Wall,
  WallType,
  MovementPath,
  Annotation,
  AnnotationType,
  Vec3,
  StageMetadata,
  CameraState,
} from './types';

// ============================================================================
// Vector Helper
// ============================================================================

export function createVec3(x: number = 0, y: number = 0, z: number = 0): Vec3 {
  return { x, y, z };
}

// ============================================================================
// Canvas Configuration
// ============================================================================

export function createDefaultCanvasConfig(): CanvasConfig {
  return {
    width_m: 30,
    height_m: 20,
    grid_size_m: 1,
  };
}

// ============================================================================
// Stage Objects
// ============================================================================

export function createDefaultObject(
  type: StageObjectType,
  variant?: ObjectVariant,
  position?: Partial<Vec3>,
  label?: string
): StageObject {
  const defaultPosition = { x: 0, y: 0, z: 0, ...position };

  return {
    id: uuidv4(),
    type,
    variant,
    position: defaultPosition,
    rotation: createVec3(0, 0, 0),
    scale: createVec3(1, 1, 1),
    on_stand: false,
    stand_height_m: 0,
    locked: false,
    facing_direction: 0,
    ...(label && { label }),
  };
}

export function createPaperTarget(
  position?: Partial<Vec3>,
  label?: string
): StageObject {
  return createDefaultObject('paper_target', 'ipsc_classic', position, label);
}

export function createSteelTarget(
  position?: Partial<Vec3>,
  label?: string
): StageObject {
  return createDefaultObject('steel_target', 'round_8', position, label);
}

export function createPopper(
  position?: Partial<Vec3>,
  label?: string
): StageObject {
  const obj = createDefaultObject('popper', 'standard', position, label);
  obj.activation = {
    type: 'static',
  };
  return obj;
}

export function createPlate(
  position?: Partial<Vec3>,
  label?: string
): StageObject {
  const obj = createDefaultObject('plate', 'standard', position, label);
  obj.activation = {
    type: 'static',
  };
  return obj;
}

export function createSwinger(
  position?: Partial<Vec3>,
  label?: string
): StageObject {
  const obj = createDefaultObject('swinger', 'round_8', position, label);
  obj.activation = {
    type: 'swinger',
    swing_arc_degrees: 90,
  };
  return obj;
}

export function createDropTurner(
  position?: Partial<Vec3>,
  label?: string
): StageObject {
  const obj = createDefaultObject('drop_turner', 'round_8', position, label);
  obj.activation = {
    type: 'drop_turner',
    exposure_time: 2,
  };
  return obj;
}

export function createTexasStar(
  position?: Partial<Vec3>,
  label?: string
): StageObject {
  const obj = createDefaultObject('texas_star', 'standard', position, label);
  obj.on_stand = true;
  obj.stand_height_m = 1.5;
  obj.activation = {
    type: 'swinger',
    swing_arc_degrees: 360,
  };
  return obj;
}

export function createBobber(
  position?: Partial<Vec3>,
  label?: string
): StageObject {
  const obj = createDefaultObject('bobber', 'mini', position, label);
  obj.activation = {
    type: 'bobber',
    timing_seconds: 3,
  };
  return obj;
}

export function createBarrel(
  position?: Partial<Vec3>,
  label?: string
): StageObject {
  return createDefaultObject('barrel', 'standard', position, label);
}

export function createTable(
  position?: Partial<Vec3>,
  label?: string
): StageObject {
  const obj = createDefaultObject('table', 'rectangular', position, label);
  obj.on_stand = true;
  obj.stand_height_m = 0.75;
  return obj;
}

export function createFaultLine(
  position?: Partial<Vec3>,
  label?: string
): StageObject {
  return createDefaultObject('fault_line', undefined, position, label);
}

export function createCone(
  position?: Partial<Vec3>,
  label?: string
): StageObject {
  return createDefaultObject('cone', 'standard', position, label);
}

export function createNoShoot(
  position?: Partial<Vec3>,
  label?: string
): StageObject {
  return createDefaultObject('no_shoot', 'ipsc_classic', position, label);
}

export function createHardCover(
  position?: Partial<Vec3>,
  label?: string
): StageObject {
  return createDefaultObject('hard_cover', undefined, position, label);
}

export function createSoftCover(
  position?: Partial<Vec3>,
  label?: string
): StageObject {
  return createDefaultObject('soft_cover', undefined, position, label);
}

export function createChair(
  position?: Partial<Vec3>,
  label?: string
): StageObject {
  return createDefaultObject('chair', 'standard', position, label);
}

export function createDoor(
  position?: Partial<Vec3>,
  label?: string
): StageObject {
  return createDefaultObject('door', 'standard', position, label);
}

export function createWindow(
  position?: Partial<Vec3>,
  label?: string
): StageObject {
  return createDefaultObject('window', 'standard', position, label);
}

export function createBed(
  position?: Partial<Vec3>,
  label?: string
): StageObject {
  return createDefaultObject('bed', 'standard', position, label);
}

export function createCouch(
  position?: Partial<Vec3>,
  label?: string
): StageObject {
  return createDefaultObject('couch', 'standard', position, label);
}

export function createCar(
  position?: Partial<Vec3>,
  label?: string
): StageObject {
  return createDefaultObject('car', 'standard', position, label);
}

export type ShooterPose =
  | 'shooter_relaxed'
  | 'shooter_hands_up'
  | 'shooter_seated'
  | 'shooter_broom';

export function createShooter(
  pose: ShooterPose = 'shooter_relaxed',
  position?: Partial<Vec3>,
  label?: string,
): StageObject {
  return createDefaultObject('shooter', pose, position, label);
}

export function createPaperSwinger(
  position?: Partial<Vec3>,
  label?: string,
): StageObject {
  const obj = createDefaultObject('paper_swinger', 'idpa', position, label);
  obj.activation = {
    type: 'swinger',
    swing_arc_degrees: 90,
  };
  return obj;
}

export function createPaperSwingerOnce(
  position?: Partial<Vec3>,
  label?: string,
): StageObject {
  const obj = createDefaultObject('paper_swinger_once', 'idpa', position, label);
  obj.activation = {
    type: 'swinger',
    // Signed arc: +90 swings right, -90 swings left. Single shot.
    swing_arc_degrees: 90,
  };
  return obj;
}

export function createPaperPopup(
  position?: Partial<Vec3>,
  label?: string,
): StageObject {
  const obj = createDefaultObject('paper_popup', 'idpa', position, label);
  obj.activation = {
    type: 'bobber',
    timing_seconds: 3,
  };
  return obj;
}

// ============================================================================
// Shooting Positions
// ============================================================================

export function createDefaultShootingPosition(
  type: ShootingPositionType = 'start_position',
  position?: Partial<Vec3>,
  label?: string
): ShootingPosition {
  const defaultPosition = { x: 0, y: 0, z: 0, ...position };

  const shootingPosition: ShootingPosition = {
    id: uuidv4(),
    type,
    position: defaultPosition,
    width_m: 1.5,
    depth_m: 1.5,
    rotation: 0,
    label: label || type.replace(/_/g, ' '),
    is_start: type === 'start_position',
  };

  if (type === 'start_position') {
    shootingPosition.start_condition = 'hands_relaxed';
  }

  return shootingPosition;
}

export function createStartPosition(
  condition?: StartCondition,
  position?: Partial<Vec3>,
  label?: string
): ShootingPosition {
  const pos = createDefaultShootingPosition('start_position', position, label);
  if (condition) {
    pos.start_condition = condition;
  }
  return pos;
}

export function createShootingBox(
  position?: Partial<Vec3>,
  label?: string
): ShootingPosition {
  return createDefaultShootingPosition('shooting_box', position, label);
}

export function createFreeFireZone(
  position?: Partial<Vec3>,
  label?: string
): ShootingPosition {
  return createDefaultShootingPosition('free_fire_zone', position, label);
}

// ============================================================================
// Walls
// ============================================================================

export function createDefaultWall(
  type: WallType = 'wall',
  points?: Partial<Vec3>[],
  _label?: string
): Wall {
  const defaultPoints: Vec3[] = points
    ? points.map((p) => ({ x: 0, y: 0, z: 0, ...p }))
    : [
        { x: 0, y: 0, z: 0 },
        { x: 5, y: 0, z: 0 },
      ];

  return {
    id: uuidv4(),
    type,
    points: defaultPoints,
    height_m: 2,
    has_port: false,
    material: 'plywood',
  };
}

export function createWallWithPort(
  type: WallType = 'port_wall',
  points?: Partial<Vec3>[],
  portHeight?: number,
  portWidth?: number
): Wall {
  const wall = createDefaultWall(type, points);
  wall.has_port = true;
  wall.port_config = {
    height_m: portHeight || 0.5,
    width_m: portWidth || 1,
    offset_from_ground_m: 0.8,
  };
  wall.material = 'plywood';
  return wall;
}

export function createBarrelStack(
  points?: Partial<Vec3>[]
): Wall {
  return createDefaultWall('barrel_stack', points);
}

export function createMeshFence(
  points?: Partial<Vec3>[]
): Wall {
  const wall = createDefaultWall('mesh_fence', points);
  wall.material = 'mesh';
  return wall;
}

export function createVisionBarrier(
  points?: Partial<Vec3>[]
): Wall {
  return createDefaultWall('vision_barrier', points);
}

export function createBerm(
  points?: Partial<Vec3>[]
): Wall {
  const wall = createDefaultWall('berm', points);
  wall.material = 'concrete';
  return wall;
}

// ============================================================================
// Movement Paths
// ============================================================================

export function createDefaultMovementPath(
  waypoints?: Partial<Vec3>[],
  label?: string
): MovementPath {
  const defaultWaypoints: Vec3[] = waypoints
    ? waypoints.map((wp) => ({ x: 0, y: 0, z: 0, ...wp }))
    : [
        { x: 0, y: 0, z: 0 },
        { x: 5, y: 0, z: 0 },
      ];

  return {
    id: uuidv4(),
    waypoints: defaultWaypoints,
    ...(label && { label }),
  };
}

// ============================================================================
// Annotations
// ============================================================================

export function createDefaultAnnotation(
  type: AnnotationType = 'text',
  position?: Partial<Vec3>,
  text?: string
): Annotation {
  const defaultPosition = { x: 0, y: 0, z: 0, ...position };

  return {
    id: uuidv4(),
    type,
    position: defaultPosition,
    ...(text && { text }),
    ...(type === 'text' && { font_size: 12 }),
  };
}

export function createTextAnnotation(
  text: string,
  position?: Partial<Vec3>,
  fontSize?: number
): Annotation {
  const annotation = createDefaultAnnotation('text', position, text);
  annotation.font_size = fontSize || 12;
  return annotation;
}

export function createMeasurementAnnotation(
  points?: Partial<Vec3>[],
  position?: Partial<Vec3>
): Annotation {
  const defaultPosition = { x: 0, y: 0, z: 0, ...position };
  const defaultPoints: Vec3[] = points
    ? points.map((p) => ({ x: 0, y: 0, z: 0, ...p }))
    : [
        { x: 0, y: 0, z: 0 },
        { x: 5, y: 0, z: 0 },
      ];

  return {
    id: uuidv4(),
    type: 'measurement',
    position: defaultPosition,
    points: defaultPoints,
    value: 5,
  };
}

export function createAngleAnnotation(
  position?: Partial<Vec3>,
  rotation?: number
): Annotation {
  const annotation = createDefaultAnnotation('angle', position);
  if (rotation !== undefined) {
    annotation.rotation = rotation;
  }
  annotation.value = rotation || 0;
  return annotation;
}

export function createNorthArrow(
  position?: Partial<Vec3>,
  rotation?: number
): Annotation {
  const annotation = createDefaultAnnotation('north_arrow', position);
  if (rotation !== undefined) {
    annotation.rotation = rotation;
  }
  return annotation;
}

export function createBoundaryAnnotation(
  points?: Partial<Vec3>[],
  position?: Partial<Vec3>
): Annotation {
  const defaultPosition = { x: 0, y: 0, z: 0, ...position };
  const defaultPoints: Vec3[] = points
    ? points.map((p) => ({ x: 0, y: 0, z: 0, ...p }))
    : [
        { x: 0, y: 0, z: 0 },
        { x: 10, y: 0, z: 0 },
        { x: 10, y: 10, z: 0 },
        { x: 0, y: 10, z: 0 },
      ];

  return {
    id: uuidv4(),
    type: 'boundary',
    position: defaultPosition,
    points: defaultPoints,
  };
}

// ============================================================================
// Metadata
// ============================================================================

export function createDefaultMetadata(name: string = 'Unnamed Stage'): StageMetadata {
  return {
    name,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
}

// ============================================================================
// Camera State
// ============================================================================

export function createDefaultCameraState(): CameraState {
  return {
    position: { x: 15, y: 10, z: 20 },
    target: { x: 15, y: 10, z: 0 },
    zoom: 1,
  };
}

// ============================================================================
// Complete Stage Layout
// ============================================================================

export function createDefaultLayout(name: string = 'Unnamed Stage'): StageLayout {
  return {
    version: '1.0.0',
    canvas: createDefaultCanvasConfig(),
    objects: [],
    shooting_positions: [createStartPosition(undefined, { x: 5, y: 5, z: 0 }, 'Start Position')],
    walls: [],
    movement_paths: [],
    annotations: [createNorthArrow({ x: 28, y: 18, z: 0 })],
    metadata: createDefaultMetadata(name),
    camera: createDefaultCameraState(),
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Creates a complete stage with example components for demonstration/testing
 */
export function createExampleLayout(): StageLayout {
  const layout = createDefaultLayout('Example Stage');

  // Add some target objects
  layout.objects.push(
    createPaperTarget({ x: 8, y: 5, z: 0 }, 'Paper Target 1'),
    createSteelTarget({ x: 12, y: 5, z: 0 }, 'Steel Target 1'),
    createSwinger({ x: 16, y: 5, z: 0 }, 'Swinger 1'),
    createPopper({ x: 8, y: 10, z: 0 }, 'Popper 1'),
    createTexasStar({ x: 15, y: 10, z: 0 }, 'Texas Star')
  );

  // Add shooting positions
  layout.shooting_positions.push(
    createShootingBox({ x: 5, y: 12, z: 0 }, 'Box 1'),
    createFreeFireZone({ x: 15, y: 15, z: 0 }, 'Free Fire Zone 1')
  );

  // Add a wall
  layout.walls.push(
    createDefaultWall('wall', [
      { x: 25, y: 0, z: 0 },
      { x: 25, y: 20, z: 0 },
    ])
  );

  return layout;
}
